if (!customElements.get('facets-bar')) {
  // Filters, counts and pages the rendered list in the browser; only a new sort order reloads the section
  class FacetsBar extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.perPage = parseInt(this.dataset.perPage, 10) || 24;

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('submit', this.onSubmit.bind(this));
      this.addEventListener('toggle', this.onToggle.bind(this), true);
      this.addEventListener('keyup', this.onKeyUp.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('keydown', this.onKeyDown.bind(this));
      this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
      document.addEventListener('click', this.onDocumentClick.bind(this));
      window.addEventListener('popstate', () => {
        this.state = this.readState();
        this.apply();
      });
      this.addEventListener('click', this.rememberProduct.bind(this));
      window.addEventListener('pageshow', this.restoreFocus.bind(this));

      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      const params = new URLSearchParams(window.location.search);
      if (params.has('page')) {
        // Old links page on the server; the list now holds every product on one server page
        params.set('pg', params.get('page'));
        params.delete('page');
        window.location.replace(`${window.location.pathname}?${params}`);
        return;
      }

      this.setUp();
      this.state = this.readState();
      this.apply();
      this.classList.remove('is-pending');
    }

    setUp() {
      this.items = [...this.querySelectorAll('[data-facets-item]')].map((element) => ({
        element,
        data: JSON.parse(element.dataset.facetsItem),
      }));
      this.promos = [...this.querySelectorAll('[data-facets-promo]')];
      this.known = new Map();
      this.querySelectorAll('[data-facet-input]').forEach((input) => {
        if (!this.known.has(input.name)) this.known.set(input.name, new Set());
        this.known.get(input.name).add(input.value);
      });
      this.groups = [...this.known.keys()];
      this.price = this.querySelector('[data-facets-price]');
    }

    readState() {
      const params = new URLSearchParams(window.location.search);
      const number = (key) => {
        const value = parseInt(params.get(key), 10);
        return Number.isNaN(value) ? null : value;
      };
      return {
        values: Object.fromEntries(
          this.groups.map((group) => [group, new Set(params.getAll(group).filter((value) => this.known.get(group).has(value)))])
        ),
        priceFrom: this.price ? number('price_from') : null,
        priceTo: this.price ? number('price_to') : null,
        page: Math.max(number('pg') || 1, 1),
      };
    }

    get activeCount() {
      const values = Object.values(this.state.values).reduce((sum, set) => sum + set.size, 0);
      return values + (this.hasPrice ? 1 : 0);
    }

    get hasPrice() {
      return this.state.priceFrom !== null || this.state.priceTo !== null;
    }

    matches(item, skipGroup = null) {
      for (const [group, chosen] of Object.entries(this.state.values)) {
        if (group === skipGroup || chosen.size === 0) continue;
        if (!(item.data[group] || []).some((value) => chosen.has(value))) return false;
      }
      const price = item.data.price / 100;
      if (this.state.priceFrom !== null && price < this.state.priceFrom) return false;
      if (this.state.priceTo !== null && price > this.state.priceTo) return false;
      return true;
    }

    plural(count, one, other) {
      return count === 1 ? one : other.replace('1000000', count);
    }

    formatPrice(value) {
      const money = this.dataset.money || '0';
      return money.replace('0', new Intl.NumberFormat(document.documentElement.lang || undefined).format(value));
    }

    apply({ updateHistory = false, announce = false } = {}) {
      const matching = this.items.filter((item) => this.matches(item));
      const count = matching.length;
      const pages = Math.max(Math.ceil(count / this.perPage), 1);
      this.state.page = Math.min(this.state.page, pages);

      this.updateValues();
      this.updateGroups();

      const countLabel = this.plural(count, this.dataset.countOne, this.dataset.countOther);
      this.querySelectorAll('[data-facets-count]').forEach((element) => (element.textContent = countLabel));
      const submit = this.querySelector('[data-facets-submit] span');
      if (submit) submit.textContent = this.plural(count, this.dataset.resultsOne, this.dataset.resultsOther);
      const reset = this.querySelector('[data-facets-reset]');
      if (reset) reset.disabled = this.activeCount === 0;
      if (announce) {
        const status = this.querySelector('[data-facets-status]');
        if (status) status.textContent = countLabel;
      }

      const start = (this.state.page - 1) * this.perPage;
      const visible = new Set(matching.slice(start, start + this.perPage));
      this.items.forEach((item) => {
        item.element.hidden = !visible.has(item);
        if (item.element.hidden) return;
        const card = item.element.querySelector(':scope > template[data-facets-card]');
        card?.replaceWith(card.content);
      });
      const unfiltered = this.activeCount === 0 && this.state.page === 1;
      this.promos.forEach((promo) => (promo.hidden = !unfiltered));

      const empty = this.querySelector('[data-facets-empty]');
      if (empty) empty.hidden = count > 0 || this.items.length === 0;
      this.querySelector('.collection-grid__list')?.toggleAttribute('hidden', count === 0);
      this.querySelector('.collection-grid__count')?.toggleAttribute('hidden', count === 0);
      this.renderPagination(pages);

      if (updateHistory) this.writeState();
    }

    updateValues() {
      const counts = Object.fromEntries(
        this.groups.map((group) => {
          const tally = new Map();
          this.items.forEach((item) => {
            if (!this.matches(item, group)) return;
            (item.data[group] || []).forEach((value) => tally.set(value, (tally.get(value) || 0) + 1));
          });
          return [group, tally];
        })
      );

      this.querySelectorAll('[data-facet-input]').forEach((input) => {
        const checked = this.state.values[input.name].has(input.value);
        const unavailable = !checked && !counts[input.name].get(input.value);
        input.checked = checked;
        input.disabled = unavailable;
        const pill = this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        pill?.classList.toggle('pill--unavailable', unavailable);
        pill?.querySelector('[data-facet-unavailable]')?.toggleAttribute('hidden', !unavailable);
      });

      this.querySelectorAll('[data-facet-price]').forEach((input) => {
        if (document.activeElement === input) return;
        const from = input.name === 'price_from';
        const value = from ? this.state.priceFrom : this.state.priceTo;
        input.value = value ?? this.price.dataset[from ? 'min' : 'max'];
      });

      this.querySelectorAll('[data-facet-range]').forEach((range) => {
        const from = range.dataset.facetRange === 'price_from';
        const value = from ? this.state.priceFrom : this.state.priceTo;
        range.value = value ?? range[from ? 'min' : 'max'];
      });
      this.querySelectorAll('[data-range-slider]').forEach((slider) => this.updateRange(slider));
    }

    updateRange(slider) {
      const [from, to] = slider.querySelectorAll('[data-facet-range]');
      const min = parseInt(from.min, 10);
      const span = parseInt(from.max, 10) - min || 1;
      slider.style.setProperty('--range-from', (from.value - min) / span);
      slider.style.setProperty('--range-to', (to.value - min) / span);
      slider.classList.toggle('range-slider--from-top', from.value - min > span / 2);
      [from, to].forEach((range) => {
        const key = range === from ? 'min' : 'max';
        const atEnd = range.value === range[key];
        range.setAttribute('aria-valuetext', this.formatPrice(atEnd ? this.price.dataset[key] : range.value));
      });
    }

    onInput(event) {
      const range = event.target.closest('[data-facet-range]');
      if (!range) return;
      const slider = range.closest('[data-range-slider]');
      const [from, to] = slider.querySelectorAll('[data-facet-range]');
      if (parseInt(from.value, 10) > parseInt(to.value, 10)) range.value = range === from ? to.value : from.value;
      this.updateRange(slider);
      const key = range === from ? 'min' : 'max';
      const field = this.price.querySelector(`[data-facet-price][name="${range.dataset.facetRange}"]`);
      if (field) field.value = range.value === range[key] ? this.price.dataset[key] : range.value;
    }

    onKeyDown(event) {
      const range = event.target.closest('[data-facet-range]');
      if (!range || (event.key !== 'PageUp' && event.key !== 'PageDown')) return;
      event.preventDefault();
      const value = parseInt(range.value, 10) + (event.key === 'PageUp' ? 100 : -100);
      range.value = Math.min(Math.max(value, parseInt(range.min, 10)), parseInt(range.max, 10));
      range.dispatchEvent(new Event('input', { bubbles: true }));
      range.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setCountBubble(element, count) {
      element.hidden = count === 0;
      element.querySelector('.count-bubble').textContent = count;
      element.querySelector('.visually-hidden').textContent = this.plural(
        count,
        element.dataset.labelOne,
        element.dataset.labelOther
      );
    }

    updateGroups() {
      const active = this.activeCount;
      this.querySelectorAll('[data-facets-active-count]').forEach((element) => this.setCountBubble(element, active));
      this.querySelector('[data-facets-toggle]')?.classList.toggle('is-active', active > 0);

      this.querySelectorAll('[data-facets-group]').forEach((group) => {
        const name = group.dataset.facetsGroup;
        const size = name === 'price' ? (this.hasPrice ? 1 : 0) : this.state.values[name]?.size || 0;
        group.querySelector('[data-facets-group-toggle]')?.classList.toggle('is-active', size > 0);
        group.querySelectorAll('[data-facets-group-count]').forEach((element) => this.setCountBubble(element, size));
        group.querySelectorAll('.facets-sheet__reset').forEach((button) => (button.hidden = size === 0));
        const footer = group.querySelector('[data-facets-group-footer]');
        if (footer) {
          footer.hidden = size === 0;
          const selected = footer.querySelector('[data-facets-selected]');
          selected.textContent = this.plural(size, selected.dataset.labelOne, selected.dataset.labelOther);
        }
        if (group.matches('.facets-sheet__group') && size > 0 && !this.opened) group.open = true;
      });
      this.opened = true;

      let chips = 0;
      this.querySelectorAll('[data-facets-chip]').forEach((chip) => {
        const group = chip.dataset.facetsChip;
        let show;
        if (group === 'price') {
          show = this.hasPrice;
          if (show) {
            const from = this.state.priceFrom ?? parseInt(this.price.dataset.min, 10);
            const to = this.state.priceTo ?? parseInt(this.price.dataset.max, 10);
            const label = `${this.formatPrice(from)} – ${this.formatPrice(to)}`;
            chip.querySelector('[data-facets-chip-label]').textContent = label;
            const button = chip.querySelector('button');
            button.setAttribute('aria-label', button.dataset.label.replace('[value]', label));
          }
        } else {
          show = this.state.values[group]?.has(chip.dataset.value) || false;
        }
        chip.hidden = !show;
        if (show) chips += 1;
      });
      this.querySelector('[data-facets-chips]')?.toggleAttribute('hidden', chips === 0);
      this.querySelector('[data-facets-chips-divider]')?.toggleAttribute('hidden', chips === 0);
    }

    renderPagination(pages) {
      const container = this.querySelector('[data-facets-pagination]');
      if (!container) return;
      container.querySelector('.pagination-scale')?.remove();
      if (pages < 2) return;

      const current = this.state.page;
      const nav = container.querySelector('template').content.firstElementChild.cloneNode(true);
      const ticks = nav.querySelector('.pagination-scale__ticks');
      const tickTemplate = ticks.firstElementChild;
      tickTemplate.remove();
      for (let page = 1; page <= pages; page += 1) {
        const tick = tickTemplate.cloneNode(true);
        const link = tick.querySelector('a');
        link.href = this.pageUrl(page);
        link.dataset.page = page;
        link.setAttribute('aria-label', link.getAttribute('aria-label').replace(/\d+/, page));
        if (page === current) {
          tick.classList.add('pagination-scale__tick--current');
          link.setAttribute('aria-current', 'page');
        }
        ticks.append(tick);
      }
      nav.querySelector('.pagination-scale__label').textContent = container.dataset.labelOf
        .replace('[current]', current)
        .replace('[total]', pages);
      nav.querySelectorAll('[data-facets-page-step]').forEach((link) => {
        const page = current + parseInt(link.dataset.facetsPageStep, 10);
        const disabled = nav.querySelector(`[data-facets-page-disabled="${link.dataset.facetsPageStep}"]`);
        if (page >= 1 && page <= pages) {
          link.href = this.pageUrl(page);
          link.dataset.page = page;
          disabled.remove();
        } else {
          link.remove();
        }
      });
      container.append(nav);
    }

    pageUrl(page) {
      const params = this.stateParams();
      if (page > 1) params.set('pg', page);
      else params.delete('pg');
      const search = params.toString();
      return `${window.location.pathname}${search ? `?${search}` : ''}`;
    }

    stateParams() {
      const params = new URLSearchParams(window.location.search);
      [...this.groups, 'price_from', 'price_to', 'pg'].forEach((key) => params.delete(key));
      Object.entries(this.state.values).forEach(([group, values]) => values.forEach((value) => params.append(group, value)));
      if (this.state.priceFrom !== null) params.set('price_from', this.state.priceFrom);
      if (this.state.priceTo !== null) params.set('price_to', this.state.priceTo);
      if (this.state.page > 1) params.set('pg', this.state.page);
      return params;
    }

    writeState() {
      const search = this.stateParams().toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
    }

    clear(group = null) {
      if (group === 'price' || group === null) {
        this.state.priceFrom = null;
        this.state.priceTo = null;
      }
      Object.entries(this.state.values).forEach(([name, values]) => {
        if (group === null || name === group) values.clear();
      });
      this.state.page = 1;
      this.apply({ updateHistory: true, announce: true });
    }

    rememberProduct(event) {
      const link = event.target.closest('.card-product__link');
      if (!link) return;
      try {
        sessionStorage.setItem(
          'facets-bar:last-product',
          JSON.stringify({ list: window.location.href, url: link.getAttribute('href'), keyboard: event.detail === 0 })
        );
      } catch (error) {}
    }

    restoreFocus() {
      let last = null;
      try {
        last = JSON.parse(sessionStorage.getItem('facets-bar:last-product'));
        sessionStorage.removeItem('facets-bar:last-product');
      } catch (error) {}
      if (!last || last.list !== window.location.href) return;
      const link = [...this.querySelectorAll('.card-product__link')].find((item) => item.getAttribute('href') === last.url);
      link?.focus({ preventScroll: true, focusVisible: last.keyboard });
    }

    onChange(event) {
      const input = event.target;

      if (input.matches('[data-facet-sort]')) {
        input.closest('dialog')?.close();
        input.closest('details')?.removeAttribute('open');
        this.sort(input.value);
        return;
      }

      if (input.matches('[data-facet-input]')) {
        const values = this.state.values[input.name];
        input.checked ? values.add(input.value) : values.delete(input.value);
      } else if (input.matches('[data-facet-price], [data-facet-range]')) {
        const value = parseInt(input.value.replace(/[.,]\d{1,2}$/, '').replace(/\D/g, ''), 10);
        const from = (input.dataset.facetRange || input.name) === 'price_from';
        const bound = parseInt(this.price.dataset[from ? 'min' : 'max'], 10);
        const outside = from ? value <= bound : value >= bound;
        this.state[from ? 'priceFrom' : 'priceTo'] = Number.isNaN(value) || outside ? null : value;
        const { priceFrom, priceTo } = this.state;
        if (priceFrom !== null && priceTo !== null && priceFrom > priceTo) {
          this.state.priceFrom = priceTo;
          this.state.priceTo = priceFrom;
        }
      } else {
        return;
      }

      this.state.page = 1;
      this.apply({ updateHistory: true, announce: true });
    }

    onClick(event) {
      const opener = event.target.closest('[data-sheet-open]');
      if (opener) {
        const dialog = this.querySelector(`#${opener.dataset.sheetOpen}`);
        if (!dialog) return;
        dialog.showModal();
        opener.setAttribute('aria-expanded', 'true');
        dialog.addEventListener('close', () => opener.setAttribute('aria-expanded', 'false'), { once: true });
        return;
      }

      if (event.target.closest('[data-sheet-close]')) {
        event.target.closest('dialog').close();
        return;
      }

      if (event.target.matches('dialog')) {
        event.target.close();
        return;
      }

      const chip = event.target.closest('[data-facets-chip] button');
      if (chip) {
        const item = chip.closest('[data-facets-chip]');
        const group = item.dataset.facetsChip;
        if (group === 'price') this.clear('price');
        else {
          this.state.values[group].delete(item.dataset.value);
          this.state.page = 1;
          this.apply({ updateHistory: true, announce: true });
        }
        this.querySelector('[data-facets-toggle]')?.focus();
        return;
      }

      if (event.target.closest('[data-facets-clear], [data-facets-reset]')) {
        event.preventDefault();
        this.clear();
        return;
      }

      const groupReset = event.target.closest('[data-facets-group-reset]');
      if (groupReset) {
        const group = groupReset.closest('[data-facets-group]');
        this.clear(group.dataset.facetsGroup);
        group.querySelector('summary')?.focus();
        return;
      }

      const pageLink = event.target.closest('[data-facets-pagination] a[data-page]');
      if (pageLink) {
        event.preventDefault();
        this.state.page = parseInt(pageLink.dataset.page, 10);
        this.apply({ updateHistory: true });
        this.showGridTop();
      }
    }

    onSubmit(event) {
      const form = event.target.closest('[data-facets-form]');
      if (!form) return;
      event.preventDefault();
      const active = document.activeElement;
      if (active?.matches('[data-facet-price]')) active.dispatchEvent(new Event('change', { bubbles: true }));
      form.closest('dialog').close();
    }

    showGridTop() {
      const results = this.querySelector('[data-facets-results]');
      const sticky = this.querySelector('[data-facets-sticky]');
      const offset = (sticky?.offsetHeight || 0) + (parseFloat(getComputedStyle(sticky || this).top) || 0);
      const top = results.getBoundingClientRect().top + window.scrollY - offset;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
      this.querySelector('[data-facets-item]:not([hidden]) .card-product__link')?.focus({ preventScroll: true });
    }

    onToggle(event) {
      const details = event.target;
      if (!details.matches?.('[data-facet-dropdown]') || !details.open) return;
      this.querySelectorAll('[data-facet-dropdown][open]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    }

    onKeyUp(event) {
      if (event.code !== 'Escape') return;
      const details = event.target.closest('[data-facet-dropdown][open]');
      if (!details) return;
      details.open = false;
      details.querySelector('summary').focus();
    }

    onDocumentClick(event) {
      if (event.target.closest('[data-facet-dropdown]')) return;
      this.querySelectorAll('[data-facet-dropdown][open]').forEach((details) => (details.open = false));
    }

    onTouchStart(event) {
      const handle = event.target.closest('[data-sheet-handle]');
      if (!handle) return;

      const dialog = handle.closest('dialog');
      const startY = event.touches[0].clientY;
      let offset = 0;

      const onMove = (moveEvent) => {
        offset = Math.max(0, moveEvent.touches[0].clientY - startY);
        dialog.style.transform = `translateY(${offset}px)`;
      };
      const onEnd = () => {
        dialog.style.transform = '';
        if (offset > 80) dialog.close();
        handle.removeEventListener('touchmove', onMove);
        handle.removeEventListener('touchend', onEnd);
      };

      handle.addEventListener('touchmove', onMove, { passive: true });
      handle.addEventListener('touchend', onEnd);
    }

    onScroll() {
      const sticky = this.querySelector('[data-facets-sticky]');
      if (!sticky) return;
      const top = parseFloat(getComputedStyle(sticky).top) || 0;
      sticky.classList.toggle('is-stuck', Math.abs(sticky.getBoundingClientRect().top - top) < 1 && window.scrollY > 0);
    }

    async sort(sortBy) {
      const params = this.stateParams();
      params.set('sort_by', sortBy);
      params.delete('pg');
      const search = params.toString();
      const results = this.querySelector('[data-facets-results]');
      const status = this.querySelector('[data-facets-status]');
      const token = (this.renderToken = Symbol());
      const hadFocus = this.contains(document.activeElement);

      this.classList.add('is-loading');
      results?.setAttribute('aria-busy', 'true');
      if (status?.dataset.loadingText) status.textContent = status.dataset.loadingText;

      let html = null;
      try {
        html = await fetch(`${window.location.pathname}?section_id=${this.sectionId}&${search}`).then((response) =>
          response.text()
        );
      } catch (error) {
        console.error(error);
      }
      if (token !== this.renderToken) return;

      const source = html && new DOMParser().parseFromString(html, 'text/html');
      const next = source?.querySelector('facets-bar');
      if (next) {
        ['[data-facets-bar]', '[data-facets-results]'].forEach((selector) => {
          const target = this.querySelector(selector);
          const replacement = next.querySelector(selector);
          if (target && replacement) target.innerHTML = replacement.innerHTML;
        });
        this.state.page = 1;
        const current = this.stateParams();
        current.set('sort_by', sortBy);
        window.history.replaceState(window.history.state, '', `${window.location.pathname}?${current}`);
        this.setUp();
        this.opened = false;
        this.apply({ announce: true });
        if (hadFocus) {
          [...this.querySelectorAll('.facets-bar__sort summary, .facets-bar__toggle--sort')]
            .find((toggle) => toggle.offsetParent)
            ?.focus();
        }
      } else if (status) {
        status.textContent = '';
      }

      this.classList.remove('is-loading');
      results?.removeAttribute('aria-busy');
      this.onScroll();
    }
  }

  customElements.define('facets-bar', FacetsBar);
}
